import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  Heart, Phone, Clock, AlertCircle, CheckCircle2, 
  XCircle, HelpCircle, Users, Home, BookOpen,
  MessageCircle, Star, ChevronRight, Printer
} from "lucide-react";

export default function TimeOfDeath() {
  const [expanded, setExpanded] = useState(["immediate"]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white print:bg-white" data-testid="time-of-death-page">
      {/* Hero Section */}
      <section className="py-12 bg-slate-800 text-white print:bg-white print:text-black">
        <div className="container-max px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <Heart className="w-12 h-12 mx-auto mb-4 text-rose-300 print:text-rose-600" />
            <h1 className="font-serif text-3xl md:text-4xl mb-4">
              What To Do When Death Occurs
            </h1>
            <p className="text-slate-300 text-lg print:text-slate-600">
              A gentle guide for families during this sacred time.
              You are not alone—your hospice team is here to help.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center print:hidden">
              <Button variant="secondary" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print This Guide
              </Button>
              <a href="tel:703-578-7108">
                <Button className="bg-rose-600 hover:bg-rose-700">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Hospice: 703-578-7108
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container-max px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            
            {/* Immediate Steps Card */}
            <Card className="mb-8 border-2 border-rose-200 bg-rose-50/50 print:border print:bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-rose-800">
                  <AlertCircle className="w-6 h-6" />
                  Immediate Steps (Do This First)
                </CardTitle>
                <CardDescription className="text-rose-700">
                  Take a deep breath. There is no rush. Your loved one is at peace.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Badge className="bg-rose-600 text-white mt-1">1</Badge>
                    <div>
                      <p className="font-semibold text-slate-800">Stay Calm & Take Your Time</p>
                      <p className="text-slate-600 text-sm">
                        There is no emergency. You may stay with your loved one as long as you need.
                        This is a sacred time—do not feel rushed.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Badge className="bg-rose-600 text-white mt-1">2</Badge>
                    <div>
                      <p className="font-semibold text-slate-800">Call Goodwin Hospice</p>
                      <p className="text-slate-600 text-sm">
                        <a href="tel:703-578-7108" className="text-rose-600 font-bold hover:underline">
                          703-578-7108
                        </a>
                        {" "}— Available 24 hours a day, 7 days a week.
                        Let them know your loved one has passed.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Badge className="bg-rose-600 text-white mt-1">3</Badge>
                    <div>
                      <p className="font-semibold text-slate-800">Do NOT Call 911</p>
                      <p className="text-slate-600 text-sm">
                        This is an expected death under hospice care. Calling 911 may result in 
                        unwanted interventions. The hospice team will handle all necessary notifications.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Badge className="bg-rose-600 text-white mt-1">4</Badge>
                    <div>
                      <p className="font-semibold text-slate-800">Wait for the Hospice Nurse</p>
                      <p className="text-slate-600 text-sm">
                        A nurse will come to your home to confirm the death, complete paperwork, 
                        and guide you through next steps. This usually takes 1-2 hours.
                      </p>
                    </div>
                  </li>
                </ol>
              </CardContent>
            </Card>

            {/* DO and DON'T Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card className="border-green-200 bg-green-50/50 print:border print:bg-white">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-green-800 text-lg">
                    <CheckCircle2 className="w-5 h-5" />
                    What You CAN Do
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Sit with your loved one as long as you wish</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Hold their hand, talk to them, pray, or play music</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Call family members who should know</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Contact your faith leader if desired</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Light candles or perform meaningful rituals</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Cry, grieve, and express your emotions</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-red-200 bg-red-50/50 print:border print:bg-white">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-red-800 text-lg">
                    <XCircle className="w-5 h-5" />
                    What NOT To Do
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <span><strong>DO NOT call 911</strong> (unless unexpected/traumatic)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <span>Do not rush or feel you must "do something"</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <span>Do not dispose of medications yet</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <span>Do not call the funeral home before hospice arrives</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <span>Do not remove medical equipment yourself</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <span>Do not feel you must be "strong"—it's okay to grieve</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* FAQ Accordion */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5" />
                  Common Questions Families Ask
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" value={expanded} onValueChange={setExpanded}>
                  <AccordionItem value="q1">
                    <AccordionTrigger className="text-left">
                      "How do I know they have passed?"
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      <p>Signs that death has occurred include:</p>
                      <ul className="list-disc ml-6 mt-2 space-y-1">
                        <li>No breathing or chest movement</li>
                        <li>No pulse (check at wrist or neck)</li>
                        <li>No response when you speak to them or touch them</li>
                        <li>Eyes may be open or partially open</li>
                        <li>Jaw may relax and mouth may open</li>
                        <li>Skin color may change (pale, bluish, or waxy)</li>
                      </ul>
                      <p className="mt-2">
                        If you're unsure, that's okay. Call hospice and they will guide you.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="q2">
                    <AccordionTrigger className="text-left">
                      "How long until someone comes?"
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      A hospice nurse will typically arrive within 1-2 hours of your call. 
                      During this time, you may stay with your loved one. There is no rush—
                      the body will not change significantly for several hours.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="q3">
                    <AccordionTrigger className="text-left">
                      "When will the funeral home come?"
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      The funeral home will be called AFTER the hospice nurse arrives and 
                      completes the necessary paperwork. You will have time to say goodbye 
                      before the body is taken. The nurse will ask you which funeral home 
                      you have selected.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="q4">
                    <AccordionTrigger className="text-left">
                      "What about the medications?"
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      <strong>Do not dispose of medications yourself.</strong> The hospice nurse 
                      will properly dispose of all controlled substances (like morphine or oxycodone) 
                      when they arrive. This is required by law and will be documented.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="q5">
                    <AccordionTrigger className="text-left">
                      "What if it happens at night?"
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      Goodwin Hospice has nurses available 24/7. Call the same number 
                      <strong> (703-578-7108) </strong> any time, day or night. A nurse will 
                      come to your home even in the middle of the night.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="q6">
                    <AccordionTrigger className="text-left">
                      "What if I'm alone when it happens?"
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      That's okay. Many families are alone when their loved one passes. 
                      Call the hospice number, and consider calling a friend, neighbor, 
                      or family member to come be with you while you wait for the nurse.
                      You don't have to go through this alone.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="q7">
                    <AccordionTrigger className="text-left">
                      "Can I wash or dress my loved one?"
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      Yes, if you wish. Some families find it meaningful to bathe and dress 
                      their loved one. The hospice nurse can help you with this, or the funeral 
                      home can do it later. There is no wrong choice—do what feels right for you.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="q8">
                    <AccordionTrigger className="text-left">
                      "What happens after the funeral home takes the body?"
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      <p>The funeral home will handle the body with dignity and prepare for services 
                      based on your arrangements. Goodwin Hospice will:</p>
                      <ul className="list-disc ml-6 mt-2 space-y-1">
                        <li>Pick up any medical equipment within 24-48 hours</li>
                        <li>Contact you about bereavement support services</li>
                        <li>Send information about grief support groups</li>
                        <li>Check in on you in the coming weeks</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>

            {/* What Happens Next Timeline */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  What Happens Next: A Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 font-bold text-sm">
                        1
                      </div>
                      <div className="w-0.5 h-full bg-rose-200 mt-2"></div>
                    </div>
                    <div className="pb-6">
                      <p className="font-semibold">Within 1-2 hours</p>
                      <p className="text-sm text-slate-600">
                        Hospice nurse arrives, confirms death, completes paperwork, 
                        disposes of medications, and calls funeral home.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 font-bold text-sm">
                        2
                      </div>
                      <div className="w-0.5 h-full bg-rose-200 mt-2"></div>
                    </div>
                    <div className="pb-6">
                      <p className="font-semibold">Within 2-4 hours</p>
                      <p className="text-sm text-slate-600">
                        Funeral home arrives to transport the body. You may say final 
                        goodbyes before they leave.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 font-bold text-sm">
                        3
                      </div>
                      <div className="w-0.5 h-full bg-rose-200 mt-2"></div>
                    </div>
                    <div className="pb-6">
                      <p className="font-semibold">Within 24-48 hours</p>
                      <p className="text-sm text-slate-600">
                        Medical equipment company picks up hospital bed, oxygen, 
                        and other equipment.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 font-bold text-sm">
                        4
                      </div>
                    </div>
                    <div>
                      <p className="font-semibold">Coming weeks</p>
                      <p className="text-sm text-slate-600">
                        Hospice bereavement team reaches out with support resources, 
                        grief counseling options, and memorial service assistance.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Card */}
            <Card className="bg-gradient-to-r from-rose-600 to-rose-700 text-white print:bg-rose-100 print:text-rose-900">
              <CardContent className="py-8">
                <div className="text-center">
                  <Phone className="w-10 h-10 mx-auto mb-4" />
                  <h3 className="font-serif text-2xl mb-2">Goodwin Hospice Support</h3>
                  <p className="mb-4 text-rose-100 print:text-rose-700">
                    Available 24 hours a day, 7 days a week
                  </p>
                  <a href="tel:703-578-7108">
                    <Button size="lg" className="bg-white text-rose-700 hover:bg-rose-50 print:border print:border-rose-700">
                      <Phone className="w-5 h-5 mr-2" />
                      703-578-7108
                    </Button>
                  </a>
                  <p className="mt-4 text-sm text-rose-200 print:text-rose-600">
                    hospice@goodwinliving.org
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Additional Resources */}
            <div className="mt-8 text-center print:hidden">
              <p className="text-muted-foreground mb-4">Additional Resources</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/chat">
                  <Button variant="outline">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Talk to AI Assistant
                  </Button>
                </Link>
                <Link to="/resources">
                  <Button variant="outline">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Grief Resources
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline">
                    <Users className="w-4 h-4 mr-2" />
                    Contact Support Team
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
